import { useEffect, useState } from 'react';
import Login from '../../components/Auth/Login/Login';
import Register from '../../components/Auth/Registration/Registration';
import Pin from '../../components/Auth/Pin/Pin';
import CreatePin from '../../components/Auth/CreatePin/CreatePin';
import ChangePin from '../../components/Auth/ChangePin/ChangePin';
import { useTelegram } from '../../hooks/useTelegram';
import './AuthPage.css';
import api, { handleApiError, authAPI, userAPI } from '../../services/api';

import { useTranslation } from "react-i18next";

const AuthPage = ({user, onAuth, hapticFeedback, showAlert, isInTelegram, startParam }) => {
  const { i18n } = useTranslation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [isPin, setIsPin] = useState(false);
  const [pinCode, setPinCode] = useState(null);
  const [isCreatePin, setCreatePin] = useState(false);
  const [isChangePin, setChangePin] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      if(user === null) return;
      
      setLoading(true);
      hapticFeedback('impact');
      try {
        const response = await authAPI.checkTelegramExist();
        
        const shouldRequirePin = response.data.pin_code && 
                                response.data.pin_code !== "null" && 
                                response.data.pin_code !== null && 
                                response.data.pin_code.trim() !== "";

        if (shouldRequirePin) {
          setPinCode(response.data.pin_code);
          setIsPin(true);
        } else if (response.data.pin_code === "null" || response.data.pin_code === null) {
          if (onAuth) {
            onAuth({ success: true, user });
          }
          showAlert("Добро пожаловать!");
        } else {
          setCreatePin(true);
        }
      } catch (error) {
        console.error("Auth check error:", error);
        showAlert(error?.response?.data?.error);
        setIsLogin(true);
      } finally {
        setLoading(false);
      }
    };
    checkAuth();

  }, [isInTelegram]);

  const handlePin = async (formData) => {
    setLoading(true);
    hapticFeedback("impact");

    try {
      if(formData.pin === pinCode){
        if (onAuth) {
          onAuth({ success: true, user });
          showAlert("Вход выполнен успешно!");
          return true;
        }
      }
    } catch (error) {
      console.error("Pin validation error:", error);
      showAlert(error?.response?.data?.error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleChangePinFromLogin = () => {
    setIsPin(false);
    setChangePin(true);
  };

  const handleChangePin = async (action) => {
    console.log('AuthPage handleChangePin вызван с действием:', action);

    // Отправка внутреннего OTP (без ввода номера)
    if (action.type === 'request_internal_otp') {
      console.log('Отправляем внутренний OTP...');
      setLoading(true);
      hapticFeedback('impact');
      
      try {
        const response = await userAPI.resetPincodeInit();
        console.log('Ответ resetPincodeInit:', response.data);
        
        const { sended } = response.data;
        
        if (sended) {
          console.log('✅ OTP успешно отправлен');
          return true;
        } else {
          console.log('❌ OTP не отправлен');
          showAlert("Не удалось отправить СМС-код");
          return false;
        }
      } catch (err) {
        console.error('❌ Ошибка при отправке OTP:', err);
        showAlert(err?.response?.data?.error);
        return false;
      } finally {
        setLoading(false);
      }
    }
    
    // Подтверждение смены PIN-кода с OTP
    if (action.type === 'confirm_change') {
      console.log('Подтверждаем смену PIN с данными:', action.data);
      setLoading(true);
      hapticFeedback('impact');
      
      try {
        const response = await authAPI.changePincode(action.data.new_pin, null);
        console.log('Ответ смены PIN:', response.data);
        
        if (response.data.changed === true) {
          console.log('✅ PIN успешно изменен');
          setPinCode(action.data.new_pin);
          setChangePin(false);
          setIsPin(true);
          showAlert("PIN-код успешно изменен! Теперь войдите с новым PIN.");
          return true;
        } else {
          console.log('❌ PIN не изменен:', response.data);
          showAlert("Не удалось изменить PIN-код. Проверьте код из SMS.");
          return false;
        }
      } catch (error) {
        console.error('❌ Ошибка при смене PIN:', error);
        showAlert("Ошибка при изменении PIN-кода: " + (error?.response?.data?.error || ''));
        return false;
      } finally {
        setLoading(false);
      }
    }

    console.log('⚠️ Неизвестный тип действия:', action.type);
    return false;
  };

  const handleLogoutFromPin = async () => {
    setLoading(true);
    try {
      await authAPI.makeForbid();
    } catch (error) {
      window.location.reload();
    } finally {
      setLoading(false);
      window.location.reload();
    }
  };

  const handleCancelChangePin = () => {
    setChangePin(false);
    setIsPin(true);
  };

  const handleLogin = async (formData) => {
    setLoading(true);
    hapticFeedback('impact');
    try {
      const response = await authAPI.makeLoginConfirm(formData.phone, Number(formData.otp), i18n.language);
  
      const { is_logined, tries_available } = response.data;
  
      if(is_logined){ 
        setCreatePin(true);
        showAlert("Успешный вход в систему!");
        return response.data; // Возвращаем успешный результат
      } else {
        // Возвращаем данные с ошибкой для обработки в компоненте Login
        return response.data;
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // ✅ ДОБАВЛЕНО: Проверяем специфические ошибки
      if (error.response && error.response.data.error) {
        const errorData = error.response.data.error;
        
        // Проверяем на "user not found!"
        if (errorData.error === "user not found!") {
          return { error: "user not found!" };
        }
        
        // Проверяем на ошибки с попытками
        if (errorData.is_logined === false && errorData.tries_available !== undefined) {
          return errorData;
        }
        
        // Возвращаем другие ошибки как есть
        if (errorData.error) {
          return { error: errorData.error };
        }
      }
      
      // Для неизвестных ошибок показываем общее сообщение
      showAlert('Ошибка входа. Проверьте данные.');
      
      // Возвращаем информацию об ошибке
      return error.response?.data || { error: true };
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePin = async (formData) => {
    setLoading(true);
    hapticFeedback('impact');
    try {
      if(formData.new_pin.length > 0){
        const response = await authAPI.changePincode(formData.new_pin, null);
      } 
      setCreatePin(false);
      if (onAuth) {
        onAuth({ success: true, user });
      }
      showAlert("PIN-код успешно создан!");
      return true;
    } catch (err) {
      showAlert(handleApiError(err));
      if (err.response?.status == 405) {
        if (onAuth) {
          onAuth({ success: true, user });
        }
        return true;
      }
    } finally {
      console.log('PIN creation completed');
      setLoading(false);
    }
    return false;
  };

  const handleSkipPin = async () => {
    setLoading(true);
    hapticFeedback('impact');
    
    try {
      await authAPI.changePincode(null, null);
      
      if (onAuth) {
        onAuth({ success: true, user });
      }
      showAlert("Вход выполнен успешно!");
      return true;
    } catch (error) {
      console.error("Skip PIN error:", error);
      if (error.response?.status === 405) {
        if (onAuth) {
          onAuth({ success: true, user });
        }
        showAlert("Вход выполнен успешно!");
        return true;
      }
      showAlert("Ошибка при входе");
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleRequestOtp = async (formData) => {
    setLoading(true);
    hapticFeedback('impact');
    try {
      const response = await authAPI.makeLoginInit(formData.phone);
      const {sended} = response.data;
      return sended;
    } catch (err) {
      showAlert(err?.response?.data?.error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  const handleRegister = async (userData) => {
    setLoading(true);
    hapticFeedback("impact");

    try {
      console.log("Получен результат регистрации:", userData);

      if (userData.is_registered === true) {
        showAlert("Регистрация успешна! Создайте PIN-код для защиты аккаунта.");
        
        setIsLogin(true);
        setCreatePin(true);
        
        return true;
      }

      showAlert("Не удалось завершить регистрацию. Попробуйте еще раз.");
      return false;

    } catch (error) {
      console.error("Registration error:", error);
      
      if (error.response?.data?.error === "entity already registered!") {
        showAlert("Этот номер уже зарегистрирован. Войдите в систему или используйте другой номер.");
        setIsLogin(true);
      } else {
        showAlert(handleApiError(error));
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  const switchToRegister = () => {
    setIsLogin(false);
    setCreatePin(false);
    hapticFeedback('selection');
  };

  const switchToLogin = () => {
    setIsLogin(true);
    setCreatePin(false);
    hapticFeedback('selection');
  };

  return (
    <div className="min-h-screen flex flex-col bg-white pb-[env(safe-area-inset-bottom)]">
      {isPin ? (
        <Pin 
          onPin={handlePin} 
          onChangePin={handleChangePinFromLogin}
          onLogout={handleLogoutFromPin}
          loading={loading} 
        />
      ) : isChangePin ? (
        <ChangePin
          onChangePin={handleChangePin}
          onCancel={handleCancelChangePin}
          loading={loading}
        />
      ) : isLogin ? (
        isCreatePin ? (
          <CreatePin
            onCreatePin={handleCreatePin}
            onSkip={handleSkipPin}
            loading={loading}
          />
        ) : (
          <Login
            onLogin={handleLogin}
            onSwitchToRegister={switchToRegister}
            onRequestOtp={handleRequestOtp}
            loading={loading}
          />
        )
      ) : (
        <Register
          onRegister={handleRegister}
          onSwitchToLogin={switchToLogin}
          loading={loading}
          startParam={startParam}
        />
      )}
    </div>
  );
};

export default AuthPage;